import type { ActionFunctionArgs, LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Form, useActionData, useNavigation, useOutletContext } from "@remix-run/react";
import type { Locale } from "~/utils/locale";
import { detectLocale, localePath } from "~/utils/locale";
import { t } from "~/utils/translations";
import { validateEmail } from "~/utils/validation";
import { Breadcrumbs } from "~/components/storefront/Breadcrumbs";
import prisma from "~/utils/db.server";
import { sendEmail } from "~/services/email/resend.server";
import { buildContactFormNotificationEmail } from "~/services/email/templates/contactFormNotification";
import { checkRateLimit, getClientIp } from "~/utils/rateLimiter.server";
import { buildPageMeta, originFromRequest } from "~/utils/storefrontMeta";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return json(
    { locale: detectLocale(request.url), origin: originFromRequest(request) },
    {
      headers: {
        "Cache-Control": "public, max-age=3600, s-maxage=3600, stale-while-revalidate=86400",
      },
    },
  );
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = (data?.locale || "vi") as Locale;
  const origin = data?.origin || "https://diolo.com";
  return buildPageMeta({
    title: locale === "vi" ? "Liên hệ — Diolo" : "Contact — Diolo",
    description:
      locale === "vi"
        ? "Liên hệ với Diolo — chúng tôi luôn sẵn sàng hỗ trợ bạn."
        : "Get in touch with Diolo — we'd love to hear from you.",
    path: localePath("/contact", locale),
    origin,
    locale,
  });
};

interface ActionData {
  success?: boolean;
  errors?: {
    name?: string;
    email?: string;
    message?: string;
  };
}

export const action = async ({ request }: ActionFunctionArgs) => {
  // Rate-limit by IP: without this the contact form is a free SMTP relay
  // against our Resend quota — an attacker can POST arbitrary messages
  // (replyTo attacker-controlled) with no bound on volume. 5 per hour
  // matches the pattern used elsewhere (authRateLimiter) and is well
  // above any legitimate customer's send rate.
  const ip = getClientIp(request);
  const rate = await checkRateLimit(`contact:${ip}`, 5, 60 * 60 * 1000);
  if (!rate.allowed) {
    return json<ActionData>(
      { errors: { message: "Too many messages. Please try again in an hour." } },
      { status: 429, headers: { "Retry-After": String(Math.ceil(rate.retryAfterMs / 1000)) } },
    );
  }

  const formData = await request.formData();

  // Audit 2026-04-26: honeypot bot trap. See _storefront.account.register.tsx
  // for full rationale.
  if (formData.get("website")) {
    return json({ ok: true } as any, { status: 200 });
  }

  const locale = (formData.get("locale") as Locale) || detectLocale(request.url);
  const name = (formData.get("name") as string)?.trim() || "";
  const email = (formData.get("email") as string)?.trim() || "";
  const message = (formData.get("message") as string)?.trim() || "";

  const errors: ActionData["errors"] = {};
  if (!name) errors.name = t("nameRequired", locale);
  if (!email) errors.email = t("emailRequired", locale);
  else if (!validateEmail(email)) errors.email = t("invalidEmail", locale);
  if (!message) errors.message = t("messageRequired", locale);
  // Cap message length before it hits the email template — 10k chars
  // is ~8 screens of text; anything longer is almost certainly abuse.
  if (message.length > 10_000) errors.message = "Message is too long";

  if (Object.keys(errors).length > 0) {
    return json<ActionData>({ errors }, { status: 400 });
  }

  // Send contact form notification to merchant via Resend
  try {
    const shop = await prisma.shop.findFirst();
    const shopName = shop?.companyName || shop?.name || "Diolo";
    const merchantEmail = shop?.email || "hello@diolo.com";

    const { subject, html } = buildContactFormNotificationEmail({
      name,
      email,
      message,
      shopName,
    });

    await sendEmail({
      to: merchantEmail,
      subject,
      html,
      replyTo: email,
      shopName,
      shopEmail: shop?.email ?? undefined,
    });
  } catch (emailErr) {
    console.error("[Contact] Email send failed:", emailErr);
  }

  return json<ActionData>({ success: true });
};

export default function ContactPage() {
  const { locale, socialLinks } = useOutletContext<{ locale: Locale; socialLinks?: Record<string, string> | null }>();
  const socialEntries = Object.entries(socialLinks ?? {}).filter(([, url]) => !!url);
  const socialLabel = (key: string) => key.charAt(0).toUpperCase() + key.slice(1).replace(/tok$/i, "Tok");
  const actionData = useActionData<ActionData>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  return (
    <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-12">
      <Breadcrumbs locale={locale} items={[{ label: t("contact", locale) }]} />
      {/* Header */}
      <div className="text-center mb-12">
        <p className="text-xs uppercase tracking-widest text-luxury-stone mb-4">
          {t("getInTouch", locale)}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-luxury-charcoal tracking-normal">
          {t("contactUs", locale)}
        </h1>
        <p className="mt-4 max-w-xl mx-auto text-luxury-stone text-sm leading-relaxed">
          {t("contactDescription", locale)}
        </p>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
          {/* Contact Info */}
          <div className="md:col-span-2">
            <div className="space-y-6">
              <div>
                <h3 className="text-xs uppercase tracking-widest text-luxury-charcoal mb-2">Email</h3>
                <a
                  href="mailto:hello@diolo.com"
                  className="text-sm text-luxury-stone hover:text-luxury-charcoal transition-colors"
                >
                  hello@diolo.com
                </a>
              </div>
              <div>
                <h3 className="text-xs uppercase tracking-widest text-luxury-charcoal mb-2">
                  {t("supportHours", locale)}
                </h3>
                <p className="text-sm text-luxury-stone">
                  {locale === "vi" ? "Thứ 2 – Thứ 6, 9:00 – 18:00" : "Mon – Fri, 9:00 AM – 6:00 PM"}
                </p>
                <p className="text-sm text-luxury-stone">
                  {locale === "vi" ? "(Giờ Việt Nam)" : "(Vietnam Time)"}
                </p>
              </div>
              {socialEntries.length > 0 && (
                <div>
                  <h3 className="text-xs uppercase tracking-widest text-luxury-charcoal mb-3">
                    {locale === "vi" ? "Theo dõi chúng tôi" : "Follow Us"}
                  </h3>
                  <div className="flex gap-4">
                    {socialEntries.map(([key, href]) => (
                      <a
                        key={key}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs uppercase tracking-widest text-luxury-stone hover:text-luxury-charcoal transition-colors"
                      >
                        {socialLabel(key)}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-3">
            {actionData?.success ? (
              <div className="bg-luxury-cream p-8 rounded-sm text-center">
                <svg className="w-10 h-10 mx-auto text-luxury-charcoal mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="font-display text-lg font-light text-luxury-charcoal mb-2">
                  {t("messageSent", locale)}
                </h3>
                <p className="text-luxury-stone text-sm">
                  {t("messageSentDesc", locale)}
                </p>
              </div>
            ) : (
              <Form method="post" className="space-y-5" noValidate>
                <input type="hidden" name="locale" value={locale} />
                {/* Honeypot — see _storefront.account.register.tsx for rationale. */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
                />
                <div>
                  <label className="block text-xs uppercase tracking-widest text-luxury-stone mb-2">
                    {locale === "vi" ? "Họ và tên" : "Full Name"}
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="sf-input w-full"
                    placeholder={locale === "vi" ? "Nguyễn Văn A" : "Your name"}
                    aria-invalid={actionData?.errors?.name ? true : undefined}
                    aria-describedby={actionData?.errors?.name ? "name-error" : undefined}
                  />
                  {actionData?.errors?.name && (
                    <p id="name-error" className="mt-1 text-xs text-red-600">{actionData.errors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-luxury-stone mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="sf-input w-full"
                    placeholder="you@example.com"
                    aria-invalid={actionData?.errors?.email ? true : undefined}
                    aria-describedby={actionData?.errors?.email ? "email-error" : undefined}
                  />
                  {actionData?.errors?.email && (
                    <p id="email-error" className="mt-1 text-xs text-red-600">{actionData.errors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-luxury-stone mb-2">
                    {locale === "vi" ? "Tin nhắn" : "Message"}
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    className="sf-input w-full resize-none"
                    placeholder={locale === "vi" ? "Nội dung tin nhắn..." : "Your message..."}
                    aria-invalid={actionData?.errors?.message ? true : undefined}
                    aria-describedby={actionData?.errors?.message ? "message-error" : undefined}
                  />
                  {actionData?.errors?.message && (
                    <p id="message-error" className="mt-1 text-xs text-red-600">{actionData.errors.message}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="sf-btn-filled w-full sm:w-auto disabled:opacity-50"
                >
                  {isSubmitting
                    ? (locale === "vi" ? "Đang gửi..." : "Sending...")
                    : (locale === "vi" ? "Gửi tin nhắn" : "Send Message")}
                </button>
              </Form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
